import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Star, StarOff, RefreshCw, Sparkles, Send, Trash2, CheckCircle2,
  XCircle, Loader2, ExternalLink, ShieldAlert, MessageSquareReply, Building2,
} from "lucide-react";

/**
 * Reviews page — list of Google Business Profile reviews with AI-drafted
 * replies that the owner must approve before posting. Reads from
 *   GET  /api/gbp/reviews
 *   POST /api/gbp/reviews/refresh
 *   POST /api/gbp/reviews/:id/reply/draft
 *   PUT  /api/gbp/reviews/:id/reply
 *   DELETE /api/gbp/reviews/:id/reply
 *
 * Graceful quota handling: when `api_access_status === 'pending_approval'`
 * the Send button is disabled and a status banner explains that Google's
 * GBP Reviews API quota has not yet been granted. Drafting still works.
 */
export default function Reviews() {
  const [state, setState] = useState({ loading: true, reviews: [], snapshot: null });
  const [filter, setFilter] = useState("all"); // all | unreplied | low | high
  const [refreshing, setRefreshing] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [reviewUrl, setReviewUrl] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/gbp/reviews");
      setState({
        loading: false,
        reviews: data.reviews || [],
        snapshot: data.latest_snapshot || null,
        api_access_status: data.api_access_status,
        api_access_last_error: data.api_access_last_error,
        api_access_checked_at: data.api_access_checked_at,
      });
      setReviewUrl(data.google_review_url || "");
    } catch (e) {
      setState({ loading: false, reviews: [], snapshot: null,
                   error: e.response?.data?.detail || "Failed to load reviews" });
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/gbp/reviews/refresh");
      if (data?.api_access_pending) {
        toast.error("Google review sync paused — Reviews API quota pending approval.");
      } else {
        const n = data?.new_reviews || 0;
        toast.success(n ? `Synced — ${n} new review${n === 1 ? "" : "s"}` : "Synced — no new reviews");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const visible = useMemo(() => {
    const rs = state.reviews || [];
    switch (filter) {
      case "unreplied": return rs.filter(r => !r.has_reply);
      case "low":       return rs.filter(r => (r.star_rating || 0) <= 3);
      case "high":      return rs.filter(r => (r.star_rating || 0) >= 4);
      default:          return rs;
    }
  }, [state.reviews, filter]);

  const quotaPending = state.api_access_status === "pending_approval";

  if (state.loading) {
    return (
      <div className="p-6 sm:p-8 flex items-center gap-2 text-zinc-500" data-testid="reviews-loading">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading reviews…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="p-6 sm:p-8 max-w-3xl" data-testid="reviews-error">
        <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp">
          <div className="flex items-center gap-2 mb-2 text-red-700">
            <XCircle className="w-5 h-5"/> <div className="font-medium">Reviews unavailable</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">{state.error}</div>
          <Button onClick={() => (window.location.href = "/app/integrations")}>Go to Integrations</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-5xl" data-testid="reviews-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-emerald-700"/>
          <div>
            <h1 className="font-heading text-2xl font-bold" data-testid="reviews-heading">Google Reviews</h1>
            <p className="text-sm text-zinc-500">Monitor and reply from LeadForge — every reply is owner-approved.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {reviewUrl && (
            <a href={reviewUrl} target="_blank" rel="noreferrer"
                className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1"
                data-testid="reviews-public-link">
              <ExternalLink className="w-4 h-4"/> Public review page
            </a>
          )}
          <Button onClick={refresh} disabled={refreshing} data-testid="reviews-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <RefreshCw className="w-4 h-4 mr-1"/>}
            Refresh from Google
          </Button>
        </div>
      </div>

      {quotaPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 card-crisp flex items-start gap-3" data-testid="reviews-quota-banner">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900">
            <div className="font-medium">Google Reviews API — pending approval</div>
            <div className="mt-1">
              Your OAuth connection is working, but Google has not yet granted this app a Reviews API quota.
              You can still <strong>draft replies with AI</strong>; the Send button is disabled until Google
              approves the Reviews API access.{" "}
              <a href="https://support.google.com/business/contact/api_default"
                 target="_blank" rel="noreferrer" className="underline">Google approval form →</a>
            </div>
            {state.api_access_last_error && (
              <div className="mt-2 text-xs text-amber-800 font-mono-accent break-words">
                Last error: {state.api_access_last_error.slice(0, 240)}
              </div>
            )}
          </div>
        </div>
      )}

      {state.snapshot && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="reviews-snapshot">
          <SnapshotCard label="Average rating" value={state.snapshot.average_rating ? `${Number(state.snapshot.average_rating).toFixed(1)} ★` : "—"} />
          <SnapshotCard label="Total reviews" value={state.snapshot.total_review_count ?? state.snapshot.pulled_review_count ?? "—"} />
          <SnapshotCard label="Unreplied" value={state.snapshot.unanswered_count ?? "—"} tone={state.snapshot.unanswered_count ? "warn" : "ok"} />
          <SnapshotCard label="Avg reply time" value={state.snapshot.average_response_time_hours ? `${Number(state.snapshot.average_response_time_hours).toFixed(1)} h` : "—"} />
        </div>
      )}

      <div className="flex items-center gap-2" data-testid="reviews-filters">
        {[
          ["all", "All"],
          ["unreplied", "Unreplied"],
          ["low", "Low ratings (≤3★)"],
          ["high", "High ratings (≥4★)"],
        ].map(([k, label]) => (
          <button key={k}
            onClick={() => setFilter(k)}
            data-testid={`reviews-filter-${k}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === k
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
            }`}
          >{label}</button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-12 text-center text-zinc-500" data-testid="reviews-empty">
          No reviews yet. Click <strong>Refresh from Google</strong> after connecting on the Integrations page.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => (
            <ReviewCard key={r._id} r={r} onReply={() => setReplyFor(r)} quotaPending={quotaPending}/>
          ))}
        </div>
      )}

      <ReplyDialog
        open={!!replyFor}
        review={replyFor}
        quotaPending={quotaPending}
        onClose={(reload) => { setReplyFor(null); if (reload) load(); }}
      />
    </div>
  );
}

function SnapshotCard({ label, value, tone }) {
  const color = tone === "warn" ? "border-amber-200 bg-amber-50" : "border-zinc-200 bg-white";
  return (
    <div className={`border rounded-xl p-4 card-crisp ${color}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function Stars({ n }) {
  const full = Math.max(0, Math.min(5, Number(n) || 0));
  return (
    <span className="inline-flex items-center gap-0.5" title={`${full} / 5`}>
      {Array.from({ length: 5 }).map((_, i) =>
        i < full
          ? <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-500"/>
          : <StarOff key={i} className="w-4 h-4 text-zinc-300"/>
      )}
    </span>
  );
}

function ReviewCard({ r, onReply, quotaPending }) {
  const dt = (r.update_time || r.create_time || "");
  const displayDate = dt ? new Date(dt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 card-crisp" data-testid={`review-card-${r.review_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Stars n={r.star_rating}/>
            <span className="text-sm font-medium">{r.reviewer_display_name || "Anonymous"}</span>
            <span className="text-xs text-zinc-500 font-mono-accent">{displayDate}</span>
            {r.has_reply ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs">
                <CheckCircle2 className="w-3 h-3"/> Replied
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 text-xs">
                Unreplied
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-zinc-800 whitespace-pre-wrap">
            {r.comment || <em className="text-zinc-400">(Rating only — no comment)</em>}
          </div>
          {r.has_reply && r.reply_text && (
            <div className="mt-3 pl-3 border-l-2 border-emerald-200 text-sm text-zinc-700 whitespace-pre-wrap">
              <div className="text-[10px] uppercase text-emerald-700 tracking-widest font-mono-accent mb-1">
                Owner reply
              </div>
              {r.reply_text}
            </div>
          )}
        </div>
        <div className="shrink-0">
          <Button variant={r.has_reply ? "outline" : "default"} onClick={onReply}
                   data-testid={`reviews-draft-btn-${r.review_id}`}>
            <MessageSquareReply className="w-4 h-4 mr-1"/>
            {r.has_reply ? "Edit reply" : "Draft reply"}
            {quotaPending && !r.has_reply && <span className="ml-2 text-[10px] text-amber-800">(draft only)</span>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReplyDialog({ open, review, quotaPending, onClose }) {
  const [draft, setDraft] = useState("");
  const [extra, setExtra] = useState("");
  const [tone, setTone] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && review) {
      setDraft(review.reply_text || review.reply_draft || "");
      setExtra("");
      setTone(review.reply_draft_tone || null);
    }
  }, [open, review]);

  if (!review) return null;

  const drawDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/gbp/reviews/${review.review_id}/reply/draft`, {
        extra_context: extra || null,
      });
      setDraft(data.draft);
      setTone(data.tone);
      toast.success(`AI draft ready (${data.tone})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setDrafting(false); }
  };

  const send = async () => {
    if (!draft.trim()) { toast.error("Reply is empty"); return; }
    setSending(true);
    try {
      await api.put(`/gbp/reviews/${review.review_id}/reply`, { comment: draft.trim() });
      toast.success("Reply posted to Google");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const removeReply = async () => {
    if (!window.confirm("Delete this reply on Google?")) return;
    setDeleting(true);
    try {
      await api.delete(`/gbp/reviews/${review.review_id}/reply`);
      toast.success("Reply deleted on Google");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const toneBadge = tone === "apology" ? "bg-rose-100 text-rose-700"
                    : tone === "neutral" ? "bg-amber-100 text-amber-700"
                    : tone === "gratitude" ? "bg-emerald-100 text-emerald-700"
                    : "bg-zinc-100 text-zinc-600";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-xl" data-testid="reviews-reply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareReply className="w-5 h-5 text-emerald-700"/>
            {review.has_reply ? "Edit reply" : "Draft reply"}
          </DialogTitle>
          <DialogDescription>
            Every reply is <strong>owner-approved</strong>. AI drafts a tone-aware response that you edit before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="flex items-center gap-2 mb-1">
              <Stars n={review.star_rating}/>
              <span className="text-sm font-medium">{review.reviewer_display_name || "Anonymous"}</span>
            </div>
            <div className="text-sm text-zinc-700 whitespace-pre-wrap">
              {review.comment || <em className="text-zinc-400">(Rating only — no comment)</em>}
            </div>
          </div>

          <div>
            <Label htmlFor="reply-extra">Any extra context for the AI (optional)</Label>
            <Input id="reply-extra" value={extra} onChange={(e) => setExtra(e.target.value)}
                    placeholder="e.g. ‘offer a free detail next visit’"
                    data-testid="reviews-reply-context"/>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={drawDraft} disabled={drafting}
                     data-testid="reviews-ai-draft-btn">
              {drafting ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Sparkles className="w-4 h-4 mr-1"/>}
              Draft with AI
            </Button>
            {tone && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${toneBadge}`} data-testid="reviews-tone-badge">
                tone: {tone}
              </span>
            )}
          </div>

          <div>
            <Label htmlFor="reply-text">Reply — edit as needed</Label>
            <Textarea id="reply-text" value={draft} onChange={(e) => setDraft(e.target.value)}
                       rows={6} data-testid="reviews-reply-textarea"/>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <div>
            {review.has_reply && (
              <Button variant="outline" onClick={removeReply} disabled={deleting}
                       className="text-red-700 border-red-300 hover:bg-red-50"
                       data-testid="reviews-reply-delete-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Trash2 className="w-4 h-4 mr-1"/>}
                Delete reply
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={send} disabled={sending || !draft.trim() || quotaPending}
                     data-testid="reviews-reply-send-btn"
                     title={quotaPending ? "Google Reviews API quota pending approval" : undefined}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Send className="w-4 h-4 mr-1"/>}
              {quotaPending ? "Send disabled — quota pending" : "Approve & post to Google"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
